import { FC } from 'react'
import { List, Stack, TextField, Typography } from '@mui/material'

import { SidePanelElementProps, useSidePanelContext } from '@clara/layout-next'
import { Button } from '@clara/ui'
import { useT } from '@clara/locale'
import { AsyncState } from '@clara/hooks'
import { PixIcon, BankAccountIcon } from '@clara/icons'

import { BankNameResponse } from '@/features/reimbursements/api/get-bank-name'
import { BankInfoResponse, UserInfoResponse } from '../../../api/bank-info'
import { SettingsOption } from '../../common/settings-option'
import { getPixSidePanel } from './pix.form'
import { getTedSidePanel } from './ted.form'
import { TedPixDetails } from '../../common/ted-pix.details'

type Props = {
  getBankInfo: () => Promise<BankInfoResponse>
  bankInfo?: BankInfoResponse
  userInfoResponse?: AsyncState<UserInfoResponse>
  isEditing: boolean
  setIsEditing: (a: boolean) => void
  banksResponse: AsyncState<BankNameResponse>
  isManagerOrCompanyOwner?: boolean
}

export const BrBankInfoSidepanel: FC<SidePanelElementProps & Props> = ({
  getBankInfo,
  bankInfo,
  userInfoResponse,
  isEditing,
  setIsEditing,
  banksResponse,
  isManagerOrCompanyOwner,
}) => {
  const { t } = useT('reimbursements')

  const sidePanel = useSidePanelContext()

  return (
    <Stack>
      {isManagerOrCompanyOwner && !bankInfo && (
        <Typography
          variant="body1"
          sx={(theme) => ({
            color: theme.palette.v3.content.medium_emphasis,
            marginBottom: theme.spacing(5),
          })}
        >
          {t('bankinfo.sidepanel.coandmanagertitle')}
        </Typography>
      )}
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.v3.content.medium_emphasis,
          marginBottom: theme.spacing(5),
        })}
      >
        {t(`bankinfo.sidepanel.br${isEditing ? '.edition.' : '.'}description`)}
      </Typography>
      {bankInfo && !isEditing ? (
        <Stack>
          <TedPixDetails bankInfo={bankInfo} />
          <Stack
            direction="row"
            gap={2}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(3),
              left: theme.spacing(3),
              right: theme.spacing(3),
              bottom: 0,
              backgroundColor: theme.palette.common.white,
              zIndex: 1,
            })}
          >
            <Button sx={{ width: '208px' }} onClick={() => setIsEditing(true)}>
              {t('bankinfo.sidepanel.br.editinfo')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <>
          <TextField
            label={t('bankinfo.sidepanel.name.label')}
            variant="outlined"
            size="small"
            fullWidth
            value={userInfoResponse?.value?.name ?? ''}
            disabled={userInfoResponse?.loading}
            InputProps={{
              readOnly: true,
            }}
          />
          <Typography
            variant="body1"
            sx={(theme) => ({
              fontWeight: 600,
              color: theme.palette.v3.content.medium_emphasis,
              marginY: theme.spacing(4),
            })}
          >
            {t('bankinfo.sidepanel.br.whichpaymentmethod')}
          </Typography>
          <List>
            <SettingsOption
              title={t('pix.title')}
              description={t('pix.description')}
              icon={<PixIcon sx={{ width: '24px', height: '24px' }} />}
              onClick={() => {
                sidePanel.onOpen(
                  sidePanel.panels.concat(
                    getPixSidePanel({
                      getBankInfo,
                      bankInfo,
                    }),
                  ),
                  sidePanel.index + 1,
                )
              }}
            />
            <SettingsOption
              title={t('ted.title')}
              description={t('ted.description')}
              icon={<BankAccountIcon sx={{ width: '24px', height: '24px' }} />}
              onClick={() => {
                sidePanel.onOpen(
                  sidePanel.panels.concat(
                    getTedSidePanel({
                      getBankInfo,
                      bankInfo,
                      banksResponse,
                    }),
                  ),
                  sidePanel.index + 1,
                )
              }}
            />
          </List>
        </>
      )}
    </Stack>
  )
}
